/** Geometria hărții: mii de clădiri ca PUȚINE geometrii mari.
 *
 *  O plasă pe clădire ar fi zeci de mii de apeluri de desen — prea mult pentru
 *  o mașină fără placă video. Aici clădirile se adună pe CELULE pătrate
 *  (`CELULA_M`): o celulă = o geometrie pe culoare, deci câteva zeci de
 *  apeluri pe tot orașul, iar celulele din afara cadrului camerei nu se
 *  desenează deloc.
 *
 *  Fiecare clădire e o prismă: pereții (normale pe latură, ca muchiile să se
 *  vadă la lumină) și acoperișul plat. Fără bază — stă pe teren. Inelele se
 *  orientează aici (conturul trigonometric, curțile invers), deci pereții ies
 *  spre afară oricum le-a scris OSM; geometria se face ca într-un cadru drept,
 *  la fel ca `ExtrudeGeometry`, iar oglinda grupului `lume` o întoarce
 *  three.js singur (v. `plan/oglinda3d.ts`). */

import * as THREE from "three";
import { orientat } from "./inele";
import type { Cladire3, Inel, Suprafata3 } from "./tipuri";

export const CELULA_M = 500;

/** Adaugă la `poz`/`norm` triunghiurile unei suprafețe plane la cota `z`, cu
 *  fața în sus. */
function plan(inele: Inel[], z: number, poz: number[], norm: number[]): void {
  const contur = orientat(inele[0], true);
  if (contur.length < 3) return;
  const gauri = inele.slice(1).map((g) => orientat(g, false)).filter((g) => g.length >= 3);
  const v2 = (p: number[]) => new THREE.Vector2(p[0], p[1]);
  let triunghiuri: number[][];
  try {
    triunghiuri = THREE.ShapeUtils.triangulateShape(contur.map(v2), gauri.map((g) => g.map(v2)));
  } catch {
    return;
  }
  const toate = [contur, ...gauri].flat();
  for (const [a, b, c] of triunghiuri) {
    const A = toate[a], B = toate[b], C = toate[c];
    if (!A || !B || !C) continue;
    // în sus: triunghiul trigonometric privit de deasupra
    const sus = (B[0] - A[0]) * (C[1] - A[1]) - (B[1] - A[1]) * (C[0] - A[0]) >= 0;
    const [P, Q, R] = sus ? [A, B, C] : [A, C, B];
    poz.push(P[0], P[1], z, Q[0], Q[1], z, R[0], R[1], z);
    norm.push(0, 0, 1, 0, 0, 1, 0, 0, 1);
  }
}

/** Pereții unui inel orientat (contur trigonometric, curte invers): fiecare
 *  latură e un dreptunghi cu normala spre afară. */
function pereti(inel: Inel, h: number, poz: number[], norm: number[]): void {
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i];
    const b = inel[(i + 1) % inel.length];
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const L = Math.hypot(dx, dy);
    if (L < 1e-3) continue;
    const nx = dy / L, ny = -dx / L;
    poz.push(a[0], a[1], 0, b[0], b[1], 0, b[0], b[1], h,
             a[0], a[1], 0, b[0], b[1], h, a[0], a[1], h);
    for (let k = 0; k < 6; k++) norm.push(nx, ny, 0);
  }
}

function geometrie(poz: number[], norm: number[]): THREE.BufferGeometry | null {
  if (!poz.length) return null;
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(poz, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(norm, 3));
  g.computeBoundingSphere();
  g.computeBoundingBox();
  return g;
}

/** Clădirile unei celule, ca o singură geometrie (pereți + acoperișuri). */
export function geometrieCladiri(cladiri: readonly Cladire3[]): THREE.BufferGeometry | null {
  const poz: number[] = [];
  const norm: number[] = [];
  for (const c of cladiri) {
    const contur = orientat(c.inele[0], true);
    if (contur.length < 3) continue;
    pereti(contur, c.h, poz, norm);
    for (const g of c.inele.slice(1)) {
      const o = orientat(g, false);
      if (o.length >= 3) pereti(o, c.h, poz, norm);
    }
    plan(c.inele, c.h, poz, norm);
  }
  return geometrie(poz, norm);
}

/** Suprafețe plane la sol (străzi, verde, apă), ca o singură geometrie. */
export function geometrieSuprafete(supr: readonly Suprafata3[], z: number): THREE.BufferGeometry | null {
  const poz: number[] = [];
  const norm: number[] = [];
  for (const s of supr) plan(s.inele, z, poz, norm);
  return geometrie(poz, norm);
}

/** Cheia celulei în care cade un punct. */
export const celula = (x: number, y: number): string =>
  `${Math.floor(x / CELULA_M)}:${Math.floor(y / CELULA_M)}`;

/** Elementele, împărțite pe celule după un punct al lor. */
export function peCelule<T>(lista: readonly T[], punct: (e: T) => [number, number]): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const e of lista) {
    const [x, y] = punct(e);
    const k = celula(x, y);
    (out.get(k) ?? out.set(k, []).get(k)!).push(e);
  }
  return out;
}
